import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../shared/api';
import { API_BASE } from '../shared/constants';
import type { Filters, FoundationPayload } from '../types/biApp';

type ChecklistItem = { id: string; label_hr: string; label_en?: string };
type ExportDef = { id: string; label_hr: string };

type BankReadyPayload = {
    checklist: ChecklistItem[];
    reference_note_hr: string;
    companies: Array<{ id: number; code: string; name: string; legal_name?: string; oib?: string | null; city?: string | null; country_code?: string | null }>;
    exports: ExportDef[];
};

export function BankReadyPackModule({
    token,
    locale,
    foundation,
    filters,
}: {
    token: string;
    locale: 'hr' | 'en';
    foundation: FoundationPayload;
    filters: Filters;
}) {
    const [payload, setPayload] = useState<BankReadyPayload | null>(null);
    const [error, setError] = useState('');
    const [excludeLoanBook124, setExcludeLoanBook124] = useState(false);
    const [busy, setBusy] = useState<'pregled' | 'zaduzenja' | null>(null);

    const load = useCallback(async () => {
        setError('');
        try {
            const res = await api<BankReadyPayload>('/bank-ready-pack', token);
            setPayload(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
            setPayload(null);
        }
    }, [token]);

    useEffect(() => {
        void load();
    }, [load]);

    const companyId = filters.companyIds[0] ?? foundation.companies[0]?.id ?? 0;
    const company = foundation.companies.find((c) => c.id === companyId);
    const seat =
        company && (company.city || company.country_code)
            ? [company.city, company.country_code].filter(Boolean).join(', ')
            : '';

    async function download(kind: 'pregled' | 'zaduzenja') {
        if (!companyId) {
            setError(locale === 'en' ? 'Select a company.' : 'Odaberi tvrtku.');
            return;
        }
        setBusy(kind);
        setError('');
        try {
            const params = new URLSearchParams({ company_id: String(companyId) });
            if (kind === 'zaduzenja' && excludeLoanBook124) {
                params.set('exclude_loan_book', '1');
            }
            const res = await fetch(`${API_BASE}/bank-ready-pack/export/${kind}?${params.toString()}`, {
                headers: { Accept: 'application/octet-stream', Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(typeof data.message === 'string' ? data.message : res.statusText);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const zadBase =
                kind === 'zaduzenja' && excludeLoanBook124
                    ? `bank_ready_zaduzenja_${companyId}_bez124`
                    : `bank_ready_zaduzenja_${companyId}`;
            a.download = kind === 'pregled' ? `bank_ready_pregled_${companyId}.xlsx` : `${zadBase}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Download');
        } finally {
            setBusy(null);
        }
    }

    return (
        <section className="card">
            <div className="module-heading">
                <div>
                    <p className="eyebrow">30</p>
                    <h2>{locale === 'en' ? 'Bank Ready Pack' : 'Bank Ready Pack'}</h2>
                    <p className="muted">
                        {locale === 'en'
                            ? 'Bank submission pack: templates are filled from BI (company master, URA/IRA, registers 122–124). Review checklist items that still need external documents.'
                            : 'Paket za banku: predlošci se pune iz BI-a (matični podaci tvrtke, URA/IRA, registri 122–124). Ostale stavke kontrolne liste su dokumenti koje još treba priložiti izvana.'}
                    </p>
                </div>
            </div>
            {error ? <p className="error">{error}</p> : null}
            {!payload ? <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p> : null}
            {payload ? (
                <>
                    <p className="muted" style={{ marginBottom: '1rem' }}>
                        {locale === 'en'
                            ? 'Exports merge company master data (name, OIB, seat) with invoice aggregates and with facilities in Admin 122 (bank credits), 123 (leasing), and 124 (loan book), including active balance adjustments. For liabilities export, you can check “Without loan book (124)” to omit module 124 rows.'
                            : payload.reference_note_hr}
                    </p>
                    <div className="admin-toolbar" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <span className="muted">{locale === 'en' ? 'Company' : 'Tvrtka'}: </span>
                        <strong>{company?.code ?? companyId}</strong>
                        {company ? (
                            <span className="muted">
                                {' '}
                                — {company.legal_name || company.name}
                                {company.oib ? ` · OIB ${company.oib}` : ''}
                                {seat ? ` · ${locale === 'en' ? 'Seat' : 'Sjedište'}: ${seat}` : ''}
                            </span>
                        ) : null}
                        <button type="button" className="compact" disabled={busy !== null} onClick={() => void download('pregled')}>
                            {busy === 'pregled' ? '…' : locale === 'en' ? 'Download customer/supplier schedule' : 'Preuzmi pregled kupaca/dobavljača'}
                        </button>
                        <label
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                                margin: 0,
                                fontWeight: 700,
                                color: '#34383d',
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={excludeLoanBook124}
                                onChange={(ev) => setExcludeLoanBook124(ev.target.checked)}
                            />
                            <span className="muted" style={{ fontWeight: 700 }}>
                                {locale === 'en' ? 'Without loan book (124)' : 'Bez knjige pozajmica (124)'}
                            </span>
                        </label>
                        <button type="button" className="compact" disabled={busy !== null} onClick={() => void download('zaduzenja')}>
                            {busy === 'zaduzenja' ? '…' : locale === 'en' ? 'Download bank/leasing liabilities' : 'Preuzmi zaduženja banke/leasing'}
                        </button>
                    </div>
                    <div className="table-wrap">
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>{locale === 'en' ? 'Item' : 'Stavka'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payload.checklist.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.id}</td>
                                        <td>{locale === 'en' && row.label_en ? row.label_en : row.label_hr}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}
        </section>
    );
}
